import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { api, resolveImg } from "@/lib/api";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal, MaskLine } from "@/components/site/Reveal";
import { Heart, Users, Sparkles, Home as HomeIcon } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { EditableText } from "@/components/site/edit/EditableText";
import { SectionShell } from "@/components/site/edit/SectionShell";
import { ShareButton } from "@/components/site/ShareButton";

function initials(name) {
  const parts = name.replace("Rabbi ", "").replace("Rebbetzin ", "").split(" ");
  return parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

export default function About() {
  const ref = useRef(null);
  const { t, tl, lang } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  const VALUES = [
    { icon: HomeIcon, title: t("about.val1_t"), text: t("about.val1_d") },
    { icon: Heart, title: t("about.val2_t"), text: t("about.val2_d") },
    { icon: Users, title: t("about.val3_t"), text: t("about.val3_d") },
    { icon: Sparkles, title: t("about.val4_t"), text: t("about.val4_d") },
  ];

  const { data: staff = [] } = useQuery({
    queryKey: ["staff"],
    queryFn: async () => (await api.get("/staff")).data,
  });

  const { data: rebbe = { image_url: "", quote: {} } } = useQuery({
    queryKey: ["rebbe"],
    queryFn: async () => (await api.get("/rebbe")).data,
  });
  const rebbeQuote = (rebbe.quote?.[lang] || rebbe.quote?.en || "").trim();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="relative bg-[#F9F8F6] overflow-x-hidden" data-testid="about-page">
      <div className="grain" />
      <Navbar />

      {/* Intro */}
      <SectionShell id="about-sec-1" label="Section 1" noReorder>
      <section ref={ref} className="relative pt-32 sm:pt-40 pb-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <MaskLine delay={0.1}>
              <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]"><EditableText k="about.eyebrow" as="span" /></span>
            </MaskLine>
            <h1 className="mt-5 font-serif text-5xl sm:text-6xl leading-[0.98] tracking-tight text-[#0F172A]">
              <MaskLine delay={0.2}><EditableText k="about.title1" as="span" /></MaskLine>
              <MaskLine delay={0.3}><EditableText k="about.title2a" as="span" /> <span className="italic text-[#BE923C]"><EditableText k="about.title2b" as="span" /></span></MaskLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }}
              className="mt-7 text-base sm:text-lg text-[#475569] leading-relaxed max-w-xl"
            >
              <EditableText k="about.intro" as="span" />
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.6 }} className="mt-7">
              <ShareButton path="about" title={`${t("about.title1")} ${t("about.title2a")} ${t("about.title2b")}`} testid="about-share-btn" />
            </motion.div>
          </div>
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 1 }}
              className="relative h-[360px] sm:h-[460px] rounded-[2.5rem] overflow-hidden shadow-[0_30px_80px_rgba(15,23,42,0.15)]"
            >
              <motion.img
                style={{ y: imgY }}
                src="https://images.unsplash.com/photo-1522543558187-768b6df7c25c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
                alt="Our community"
                className="absolute inset-0 w-full h-[115%] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/40 to-transparent" />
            </motion.div>
          </div>
        </div>
      </section>
      </SectionShell>

      {/* Story */}
      <SectionShell id="about-sec-2" label="Section 2" noReorder>
      <section className="relative z-10 py-20 sm:py-24 bg-[#0F172A] text-white">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]"><EditableText k="about.story_eyebrow" as="span" /></span>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight leading-tight"><EditableText k="about.story_title" as="span" /></h2>
          </Reveal>

          <div className="mt-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {(rebbe.image_url || rebbeQuote) && (
              <Reveal className="lg:col-span-5">
                <figure data-testid="rebbe-block" className="lg:sticky lg:top-28">
                  {rebbe.image_url && (
                    <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-[#BE923C]/40 shadow-[0_30px_80px_rgba(0,0,0,0.4)]">
                      <img data-testid="rebbe-image" src={resolveImg(rebbe.image_url)} alt={t("about.rebbe_alt")} className="w-full aspect-[4/5] object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/50 to-transparent" />
                    </div>
                  )}
                  {rebbeQuote && (
                    <figcaption className="mt-6">
                      <p className="font-serif text-xl sm:text-2xl italic leading-relaxed text-white/90" data-testid="rebbe-quote">
                        <span className="text-[#BE923C] text-3xl leading-none align-top me-1">“</span>{rebbeQuote}<span className="text-[#BE923C] text-3xl leading-none align-bottom ms-0.5">”</span>
                      </p>
                      <p className="mt-3 text-sm font-semibold uppercase tracking-[0.2em] text-[#BE923C]"><EditableText k="about.rebbe_attribution" as="span" /></p>
                    </figcaption>
                  )}
                </figure>
              </Reveal>
            )}
            <div className={(rebbe.image_url || rebbeQuote) ? "lg:col-span-7" : "lg:col-span-12 max-w-4xl"}>
              <div className="space-y-6 text-white/75 text-base sm:text-lg leading-relaxed">
                <Reveal delay={0.05}><p><EditableText k="about.story1" as="span" /></p></Reveal>
                <Reveal delay={0.1}><p><EditableText k="about.story2" as="span" /></p></Reveal>
                <Reveal delay={0.15}><p><EditableText k="about.story3" as="span" /></p></Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>
      </SectionShell>

      {/* Staff */}
      <SectionShell id="about-sec-3" label="Section 3" noReorder>
      <section id="staff" data-testid="staff-section" className="relative z-10 py-20 sm:py-24 bg-[#E8E6E1]/50 border-y border-[#0F172A]/5">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]"><EditableText k="about.staff_eyebrow" as="span" /></span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight leading-tight"><EditableText k="about.staff_title" as="span" /></h2>
            <p className="mt-4 max-w-xl text-[#475569]"><EditableText k="about.staff_desc" as="span" /></p>
          </Reveal>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {staff.map((s, i) => (
              <Reveal key={s.id} delay={(i % 3) * 0.06}>
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 24 }}
                  data-testid={`staff-card-${i}`}
                  className="h-full rounded-3xl bg-white border border-[#0F172A]/5 p-7 shadow-[0_8px_30px_rgba(15,23,42,0.05)]"
                >
                  <div className="flex items-center gap-4">
                    {s.photo_url ? (
                      <img src={resolveImg(s.photo_url)} alt={s.name}
                        className="w-16 h-16 rounded-full object-cover shrink-0 border border-[#0F172A]/5" />
                    ) : (
                      <span className="w-16 h-16 rounded-full bg-gradient-to-br from-[#BE923C] to-[#9E7829] text-white flex items-center justify-center font-serif text-2xl shrink-0">
                        {initials(s.name)}
                      </span>
                    )}
                    <div>
                      <h3 className="font-serif text-xl leading-tight">{s.name}</h3>
                      <p className="text-sm font-medium text-[#8C9B86]">{tl(s, "role")}</p>
                      {s.since && <p className="text-xs text-[#475569] mt-0.5">{s.since}</p>}
                    </div>
                  </div>
                  <p className="mt-5 text-sm text-[#475569] leading-relaxed">{tl(s, "bio")}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      </SectionShell>

      <Footer />
    </div>
  );
}
